import {
  isScanRootScoped,
  isScanRootWorkCompleted,
  isScanRootWorkNeedsRework,
  scanRootHasScopeChanges,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

// Import types from schemas - we only import types, not the schemas themselves
export type { ApiScan, ScanLifecycleStatus, ScanType } from '../ApiSchemas/RecordTypes/ApiScanSchema';

/**
 * Checks if a scan lifecycle status allows editing.
 */
export const isScanLifecycleStatusEditable = (status: string) => status !== 'approved' && status !== 'rejected';

/**
 * Checks if a scan lifecycle status allows admin editing.
 */
export const isScanLifecycleStatusAdminEditable = (status: string) => status !== 'approved' && status !== 'rejected';

/**
 * Checks if a scan lifecycle status allows field user editing.
 */
export const isScanLifecycleStatusFieldUserEditable = (status: string) => status !== 'approved' && status !== 'rejected' && status !== 'inReview';

/**
 * @deprecated Use isScanLifecycleStatusEditable instead
 */
export const scanLifecycleStatusEditable = (status: string) => isScanLifecycleStatusEditable(status);

/**
 * Checks if a scan is editable based on its lifecycle status.
 */
export const isScanEditable = (scan: { scanLifecycleStatus: string } | null | undefined) => !!scan && isScanLifecycleStatusEditable(scan.scanLifecycleStatus);

/**
 * A handoff is a draft-routing action, rather than an approval or review
 * action. Keep the allowed states explicit so clients don't offer a transfer
 * the server must reject during review.
 */
export const isScanHandoffable = (scan: { scanLifecycleStatus: string } | null | undefined) =>
  !!scan && ['created', 'dispatched', 'inProgress', 'correctionsRequested'].includes(scan.scanLifecycleStatus);

/**
 * @deprecated Use isScanLifecycleStatusAdminEditable instead
 */
export const adminScanLifecycleStatusEditable = (status: string) =>
  status === 'dispatched' || status === 'inProgress' || status === 'created' || status === 'correctionsRequested' || status === 'pendingReview'
  || status === 'inReview';

export const scanLifecycleStatusAdminEditable = (status: string) => !(status === 'approved' || status === 'rejected');

/**
 * Checks if a scan is active (alias for isScanEditable).
 */
export const isScanActive = (scan: { scanLifecycleStatus: string } | null | undefined) => isScanEditable(scan);

/**
 * Checks if a scan is a progress walk.
 */
export const isProgressWalk = (scan: { scanType?: string } | null | undefined) =>
  !!scan && (scan.scanType === 'final' || scan.scanType === 'progress' || scan.scanType === 'progressRework' || scan.scanType === 'progressCompleted');

/**
 * Checks if a scan needs review.
 */
export const scanNeedsReview = (scan: { scanLifecycleStatus: string } | null | undefined) =>
  !!scan && (scan.scanLifecycleStatus === 'pendingReview' || scan.scanLifecycleStatus === 'inReview');

export type ScansByScanType = Record<'all' | 'scope' | 'changeRequest' | 'progress' | 'progressAny' | 'progressRework' | 'progressCompleted', Array<any>>;

/**
 * Categorizes and sorts scans by scan type.
 */
export const getScansByScanTypeDescending = <T extends { updatedAt: Date | string; scanType?: string; scanLifecycleStatus: string }>(
  scans: T[],
): Record<string, T[]> & { all: T[] } => {
  const scansDescending: T[] = [...scans].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const scansByScanType: any = {
    all: scansDescending,
    scope: [],
    changeRequest: [],
    progress: [],
    progressAny: [],
    progressRework: [],
    progressCompleted: [],
  };
  scansDescending.forEach(scan => {
    const scanType = scan.scanType ?? 'initial';
    if ((scanType === 'initial' || scanType === 'changeRequest') && scan.scanLifecycleStatus === 'approved') {
      scansByScanType.scope.push(scan);
    }
    if (isProgressWalk(scan)) {
      scansByScanType.progressAny.push(scan);
    }
    switch (scanType) {
      case 'changeRequest':
        scansByScanType.changeRequest.push(scan);
        break;
      case 'progress':
        scansByScanType.progress.push(scan);
        break;
      case 'progressRework':
        scansByScanType.progressRework.push(scan);
        break;
      case 'progressCompleted':
        scansByScanType.progressCompleted.push(scan);
        break;
    }
  });
  return scansByScanType;
};

/**
 * Counts the number of approved scope-producing scans (initial or change requests).
 */
export const getNumberOfApprovedScopes = <T extends { scanLifecycleStatus: string; scanType?: string }>(scans: T[]): number => {
  let count = 0;
  for (const scan of scans) {
    if (scan.scanLifecycleStatus === 'approved' && (scan.scanType === 'initial' || scan.scanType === 'changeRequest')) {
      count++;
    }
  }
  return count;
};

/**
 * Counts the number of approved change request scans.
 */
export const getNumberOfApprovedChangeRequests = <T extends { scanLifecycleStatus: string; scanType?: string }>(scans: T[]): number => {
  let count = 0;
  for (const scan of scans) {
    if (scan.scanLifecycleStatus === 'approved' && scan.scanType === 'changeRequest') {
      count++;
    }
  }
  return count;
};

const SCAN_LIFECYCLE_STATUS_SUBMITTABLE: Record<string, boolean> = {
  // these are submittable
  created: true,
  dispatched: true,
  inProgress: true,
  correctionsRequested: true,

  // these are not submittable
  approved: false,
  pendingReview: false,
  inReview: false,
  reviewed: false,
  rejected: false,
};

/**
 * Checks if a scan can be submitted based on its status and scan root state.
 */
export const canSubmitScan = (
  scan: { scanLifecycleStatus: string; scanType?: string } | undefined,
  scanRoot: ScanRootOptimized | undefined,
  baselineScanRoot?: ScanRootOptimized,
): boolean => {
  if (!scan) return false;
  if (!scanRoot) return false;

  if (!SCAN_LIFECYCLE_STATUS_SUBMITTABLE[scan.scanLifecycleStatus]) {
    return false;
  }

  switch (scan.scanType) {
    case 'initial':
      return isScanRootScoped(scanRoot);
    case 'changeRequest':
      return !!baselineScanRoot && scanRootHasScopeChanges(scanRoot, baselineScanRoot);
    case 'progressCompleted':
      return isScanRootWorkCompleted(scanRoot);
    case 'progressRework':
      return isScanRootWorkNeedsRework(scanRoot);
    default:
      return false;
  }
};

/**
 * @deprecated use canSubmitScan instead
 */
export const canSubmitScope = (scan: any, scanRoot: ScanRootOptimized, baselineScanRoot?: ScanRootOptimized) => {
  return canSubmitScan(scan, scanRoot, baselineScanRoot);
};
